import React from 'react';
import styled, { ThemeProps, ThemeProvider, css } from 'styled-components';
import { useRouter } from 'next/router';
import { MIN_WIDTH_FOR_SIDEBAR_NAVIGATION, themeState } from 'styleguide/theme';
import { Theme } from 'styleguide/types';
import { DividerLine } from 'styleguide/DividerLine';
import { observer } from 'mobx-react-lite';
import { stores } from 'state/stores';
import { P3 } from 'styleguide/Texts';
import { ThemedLogo } from 'components/svg/ThemedLogo';
import { Icon } from 'styleguide/Icons';
import { colors } from 'styleguide/colors';
import NavChevron from './assets/chevron.svg';
import { PrimaryButton, SecondaryButton } from 'styleguide/Buttons';
import { View } from 'styleguide/View';
import { Spacing } from 'styleguide/spacing';
import { NavBarMainContent } from './components/NavBarMainContent';
import { modal } from 'components/Modals/ModalManager';
import { NavBarUserSearchViewModel } from './components/NavBarUserSearchViewModel';
import Link from 'next/link';
import { isOnboardingScreenName } from 'screens/Onboarding/types';
import { exhaustiveTypeCheck } from 'hevy-shared';
import { FlexRow } from 'styleguide/Row';
import { t } from 'translations/t';

const HorizontalNavContentContainer = styled.nav`
  display: flex;
  max-width: ${MIN_WIDTH_FOR_SIDEBAR_NAVIGATION}px;
  margin: 0px auto;
  align-items: center;
  justify-content: space-between;
`;

const LeftContainer = styled.div`
  display: flex;
  flex: 1;
`;

const DesktopNavBarContainer = styled.div`
  flex-direction: column;
  display: flex;
  height: 100vh;
  width: 256px;
  min-width: 256px;
  position: sticky;
  top: 0;
  ${(props: ThemeProps<Theme>) =>
    props.theme.name === 'light' &&
    css`
      border-right-style: solid;
      border-right-width: 1px;
      border-right-color: ${colors.greyLight};
    `}
  @media (max-width: ${MIN_WIDTH_FOR_SIDEBAR_NAVIGATION}px) {
    display: none;
  }
`;

const DownloadButtonContainer = styled(Link)`
  display: flex;
  align-items: center;
  padding: ${Spacing.md - Spacing.xs}px;
  margin-inline-end: ${Spacing.md - Spacing.xs}px;
  text-decoration: none;
  @media (min-width: ${MIN_WIDTH_FOR_SIDEBAR_NAVIGATION}px) {
    display: none;
  }
`;

const LoginButtonContainer = styled(Link)`
  display: flex;
  align-items: center;
  padding: ${Spacing.md - Spacing.xs}px;
  padding-inline-end: ${Spacing.sm}px;
  text-decoration: none;
  @media (max-width: ${MIN_WIDTH_FOR_SIDEBAR_NAVIGATION}px) {
    display: none;
  }
`;

const SignupButtonContainer = styled(Link)`
  display: flex;
  align-items: center;
  padding: ${Spacing.md - Spacing.xs}px;
  padding-inline-start: ${Spacing.sm}px;
  text-decoration: none;
  @media (max-width: ${MIN_WIDTH_FOR_SIDEBAR_NAVIGATION}px) {
    display: none;
  }
`;

const UnauthenticatedLogoContainer = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  padding-top: ${Spacing.md}px;
  padding-bottom: ${Spacing.md}px;
  flex-basis: 0%;
  @media (max-width: ${MIN_WIDTH_FOR_SIDEBAR_NAVIGATION}px) {
    margin-inline-start: ${Spacing.md}px;
  }
`;
const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  padding: ${Spacing.lg}px ${Spacing.md}px ${Spacing.sm}px ${Spacing.md}px;
`;

const NavOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
`;

const OnboardingStatusContainer = styled.div`
  display: flex;
  flex: 2;
  gap: ${Spacing.md}px;
  align-items: center;
  justify-content: center;
  @media (max-width: ${MIN_WIDTH_FOR_SIDEBAR_NAVIGATION}px) {
    display: none;
  }
`;

const AppContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: 100vh;
  overflow-x: hidden;
  @media (max-width: ${MIN_WIDTH_FOR_SIDEBAR_NAVIGATION}px) {
    flex-direction: column;
  }
`;

const UnauthenticatedAppContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow-x: hidden;
`;

const MobileOnlyWrapper = styled.div`
  @media (min-width: ${MIN_WIDTH_FOR_SIDEBAR_NAVIGATION + 1}px) {
    display: none;
  }
`;

interface UnauthenticatedNavProps {
  route: string;
}

const getDownloadButtonUrl = ({
  route,
  query,
}: {
  route: string;
  query?: {
    shortId?: string;
    workoutId?: string;
    programId?: string;
    folderId?: string;
    username?: string;
  };
}): string | undefined => {
  const downloadButtonUrlFromPath: { [route: string]: string } = {
    '/routine/[shortId]': 'https://hevyapp.app.link/XCTvJJnBTvb',
    '/workout/[workoutId]': 'https://hevyapp.app.link/f5ZLrzFBTvb',
    '/program/[programId]': 'https://hevyapp.app.link/UXplsQ6QCMb',
    '/folder/[folderId]': 'https://hevyapp.app.link/4X6dU9vGBNb',
    '/user/[username]': 'https://hevyapp.app.link/FLqPYOMiXNb',
  };
  const baseUrl = downloadButtonUrlFromPath[route];

  if (!baseUrl || !query) return baseUrl;

  // Append a "$deeplink_path" URL parameter, which will allow the App
  // to navigate directly to this content if it is already installed
  const { shortId, workoutId, programId, folderId, username } = query;
  if (route.includes('routine') && !!shortId) {
    return `${baseUrl}?$deeplink_path=${encodeURIComponent(`routine/${shortId}`)}`;
  } else if (route.includes('user') && !!username) {
    return `${baseUrl}?$deeplink_path=${encodeURIComponent(`user/${username}`)}`;
  } else if (route.includes('workout') && !!workoutId) {
    return `${baseUrl}?$deeplink_path=${encodeURIComponent(`workout/${workoutId}`)}`;
  } else if (route.includes('program') && !!programId) {
    return `${baseUrl}?$deeplink_path=${encodeURIComponent(`program/${programId}`)}`;
  } else if (route.includes('folder') && !!folderId) {
    return `${baseUrl}?$deeplink_path=${encodeURIComponent(`folder/${folderId}`)}`;
  }
};
const UnauthenticatedNav = ({ route }: UnauthenticatedNavProps) => {
  const getLoginSignupButtonPaths: string[] = [
    '/routine/[shortId]',
    '/workout/[workoutId]',
    '/user/[username]',
    '/program/[programId]',
  ];

  const router = useRouter();

  const downloadButtonUrl = getDownloadButtonUrl({ route, query: router.query });

  return (
    <>
      <LeftContainer>
        <>
          <UnauthenticatedLogoContainer>
            <Link href="/">
              <ThemedLogo />
            </Link>
          </UnauthenticatedLogoContainer>
        </>
      </LeftContainer>

      {getLoginSignupButtonPaths.includes(route) && (
        <>
          <LoginButtonContainer href={`/login?postLoginPath=${router.asPath}`}>
            <PrimaryButton title={t('web.navBar.loginButton')} />
          </LoginButtonContainer>
          <SignupButtonContainer href={`/signup`}>
            <SecondaryButton title={t('web.navBar.signupButton')} />
          </SignupButtonContainer>
        </>
      )}

      {!!downloadButtonUrl && (
        <DownloadButtonContainer href={downloadButtonUrl}>
          <PrimaryButton title={t('web.navBar.downloadButton')} />
        </DownloadButtonContainer>
      )}
    </>
  );
};

interface LoggedInNavProps {
  pathname: string;
  showProCta: boolean;
}

const MobileTopNavBar = styled.div`
  overflow: hidden;
`;

const LoggedInMobileTopNavBar = observer(() => {
  return (
    <MobileTopNavBar
      style={{
        borderBottomColor: themeState.current.name === 'light' ? colors.greyLight : colors.greyDark,
        backgroundColor: themeState.current.secondary,
      }}
    >
      <View
        onClick={() => {
          modal.openMobileNavBarModal();
        }}
        style={{ padding: Spacing.md, cursor: 'pointer' }}
      >
        <Icon type="menu" />
      </View>
    </MobileTopNavBar>
  );
});

const LoggedInDesktopNav = (props: LoggedInNavProps) => {
  const userSearchVm = new NavBarUserSearchViewModel();
  return <LoggedInDesktopNavInner {...props} vm={userSearchVm} />;
};

const LoggedInDesktopNavInner = observer(
  ({ pathname, showProCta, vm }: LoggedInNavProps & { vm: NavBarUserSearchViewModel }) => (
    <DesktopNavBarContainer
      style={{
        borderRightColor: themeState.current.name === 'light' ? colors.greyLight : colors.grey,
        backgroundColor: themeState.current.secondary,
      }}
    >
      <NavOptionsContainer>
        <LogoContainer>
          <Link href="/">
            <ThemedLogo />
          </Link>
        </LogoContainer>
        <NavBarMainContent
          pathname={pathname}
          showProCta={showProCta}
          isSettingsEnabled={true}
          vm={vm}
        />
      </NavOptionsContainer>
    </DesktopNavBarContainer>
  ),
);

interface OnboardingNavProps {
  screenName: string;
}

const OnboardingNav = ({ screenName }: OnboardingNavProps) => {
  const steps: string[] = [
    t('web.navBar.onboardingSteps.verification'),
    t('web.navBar.onboardingSteps.username'),
    t('web.navBar.onboardingSteps.units'),
    t('web.navBar.onboardingSteps.plans'),
    t('web.navBar.onboardingSteps.getStarted'),
  ];
  const currentIndex = (() => {
    if (!isOnboardingScreenName(screenName)) {
      return -1;
    }

    switch (screenName) {
      case 'verification':
        return 0;
      case 'username':
        return 1;
      case 'units':
        return 2;
      case 'plans':
        return 3;
      case 'finish':
        return 4;
      default:
        return exhaustiveTypeCheck(screenName), -1;
    }
  })();

  return (
    <View style={{ flexDirection: 'row', width: '100vw' }}>
      <UnauthenticatedLogoContainer>
        <Link href="/">
          <ThemedLogo />
        </Link>
      </UnauthenticatedLogoContainer>
      <OnboardingStatusContainer>
        {steps.map((title, index) => (
          <FlexRow key={title} style={{ gap: Spacing.md }}>
            {index > 0 && <NavChevron />}
            <P3
              style={{
                fontWeight: 600,
                ...(index === currentIndex ? { color: colors.primary } : undefined),
              }}
            >
              {title}
            </P3>
          </FlexRow>
        ))}
      </OnboardingStatusContainer>

      <View style={{ flex: 1 }} />
    </View>
  );
};

const ThemedHorizontalNavBar = ({ children }: { children: JSX.Element | JSX.Element[] }) => {
  return (
    <div style={{ backgroundColor: themeState.current.secondary }}>
      <HorizontalNavContentContainer>{children}</HorizontalNavContentContainer>
      {themeState.current.name === 'light' && <DividerLine />}
    </div>
  );
};

interface HevyAppNavigationWrapperProps {
  isAuthenticated: boolean;
  children: JSX.Element | JSX.Element[];
}

export const HevyAppNavigationWrapper = observer(
  ({ isAuthenticated, children }: HevyAppNavigationWrapperProps) => {
    const router = useRouter();

    const appState = getAppState(isAuthenticated, router.route);

    const App = ((): JSX.Element => {
      switch (appState) {
        case 'welcome-to-pro':
          return <>{children}</>;
        case 'authenticated':
          return (
            <AppContainer>
              <>
                <LoggedInDesktopNav
                  pathname={router.pathname}
                  showProCta={stores.subscription.hasFetched ? !stores.subscription.isPro : false}
                />
                <MobileOnlyWrapper>
                  <ThemedHorizontalNavBar>
                    <LoggedInMobileTopNavBar />
                  </ThemedHorizontalNavBar>
                </MobileOnlyWrapper>
              </>
              {children}
            </AppContainer>
          );
        case 'password-reset':
          return (
            <UnauthenticatedAppContainer>
              <ThemedHorizontalNavBar>
                <UnauthenticatedNav route={router.route} />
              </ThemedHorizontalNavBar>
              {children}
            </UnauthenticatedAppContainer>
          );
        case 'onboarding':
          return (
            <UnauthenticatedAppContainer>
              <ThemedHorizontalNavBar>
                <OnboardingNav screenName={router.query.screenName as string} />
              </ThemedHorizontalNavBar>
              {children}
            </UnauthenticatedAppContainer>
          );
        case 'unauthenticated':
          return (
            <UnauthenticatedAppContainer>
              <ThemedHorizontalNavBar>
                <UnauthenticatedNav route={router.route} />
              </ThemedHorizontalNavBar>
              {children}
            </UnauthenticatedAppContainer>
          );
      }
    })();

    return <ThemeProvider theme={themeState.current}>{App}</ThemeProvider>;
  },
);

type AppState =
  | 'unauthenticated'
  | 'authenticated'
  | 'onboarding'
  | 'welcome-to-pro'
  | 'password-reset';

const getAppState = (isAuthenticated: boolean, route: string): AppState => {
  const isOnboarding =
    route === '/onboarding/[screenName]' || route === '/coach/onboarding/[screenName]';

  if (isOnboarding) {
    return 'onboarding';
  }

  const isOnWelcomeToPro = route === '/welcome-to-pro';

  if (isOnWelcomeToPro) {
    return 'welcome-to-pro';
  }

  const isResettingPassword = route.includes('/update_password');

  if (isResettingPassword) {
    return 'password-reset';
  }

  if (isAuthenticated) {
    return 'authenticated';
  }

  return 'unauthenticated';
};
